import { clientPreferences } from "@razmak/sim/config";
import { en, fa } from "./messages";

export type Language = "en" | "fa";

export function readLanguage(
  storage?: Pick<Storage, "getItem">,
  search = globalThis.location?.search ?? "",
): Language {
  const requested = new URLSearchParams(search).get("lang");
  if (requested === "en" || requested === "fa") return requested;
  try {
    return (storage ?? globalThis.localStorage)?.getItem(clientPreferences.storage.language) ===
      "en"
      ? "en"
      : clientPreferences.language;
  } catch {
    return clientPreferences.language;
  }
}

let language = readLanguage();
export const getLanguage = (): Language => language;

export function applyLanguage(): void {
  try {
    localStorage.setItem(clientPreferences.storage.language, language);
  } catch {
    // URL and locale links still work when storage is unavailable.
  }
  if (typeof document === "undefined" || !document.documentElement) return;
  document.documentElement.lang = language;
  // Layout stays identical in both languages; CSS isolates text direction.
  document.documentElement.dir = "ltr";
  document.getElementById("game")?.setAttribute("aria-label", tr("Razmak strategy game"));
}

export function setLanguage(value: unknown): boolean {
  if (value !== "en" && value !== "fa") return false;
  language = value;
  applyLanguage();
  return true;
}

// English source text is the catalog key. Values are inserted without translation:
// player names, chat messages, keys, and protocol identifiers retain their contents.
export function tr(key: string, ...values: unknown[]): string {
  const text = (language === "fa" ? fa : en)[key] ?? key;
  const persian = language === "fa" && Object.hasOwn(fa, key);
  const rendered = values.length
    ? text.replace(/\{(\d+)\}/g, (_match, index: string) => {
        const value = String(values[Number(index)]);
        // Isolate displayed values; names, numbers, and nested messages keep their direction.
        return persian ? `\u2068${value}\u2069` : value;
      })
    : text;
  // An RTL mark fixes Latin-first paragraphs without changing element direction.
  return persian && /\p{Script=Arabic}/u.test(rendered) ? `\u200f${rendered}` : rendered;
}
